import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { fetchAmountDetails, requestWithdrawal } from './services/paymentService';
import { WalletContext } from './WalletContext';
import './WithdrawFunds.css';

function WithdrawFunds({ onBack }) {
  const [amount, setAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [minimumWithdrawal, setMinimumWithdrawal] = useState(1000);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { walletBalance } = useContext(WalletContext);

  // Fetch amount details on component mount
  useEffect(() => {
    fetchAmountDetails()
      .then((data) => {
        setMinimumWithdrawal(data.minimum_withdrawal || 1000);
        setIsLoadingDetails(false);
      })
      .catch((error) => {
        console.error('Error fetching amount details:', error);
        setIsLoadingDetails(false);
      });
  }, []);

  const paymentMethods = [
    {
      id: 'bank_details',
      name: 'Bank',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 21H21M4 18H20V10H4V18ZM12 3L2 8H22L12 3Z" stroke="#666" strokeWidth="1.5" fill="none"/>
          <line x1="7" y1="13" x2="7" y2="15" stroke="#666" strokeWidth="1.5"/>
          <line x1="12" y1="13" x2="12" y2="15" stroke="#666" strokeWidth="1.5"/>
          <line x1="17" y1="13" x2="17" y2="15" stroke="#666" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      id: 'upi_id',
      name: 'UPI (Google Pay, Phone Pe, Paytm...)',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
          <path d="m2.16,14.54h.15l-.14.58c-.02.06-.02.13.01.2.04.05.1.07.16.06.07,0,.14-.02.19-.06.05-.05.09-.12.11-.2l.14-.58h.15l-.14.6c-.02.11-.08.22-.17.29-.09.07-.2.1-.31.1-.13,0-.22-.03-.26-.09s-.06-.16-.03-.29l.14-.6Z"/><path d="m2.91,15.49l.24-.99.45.59s.02.03.04.05.02.04.04.06l.16-.66h.14l-.24.99-.46-.6-.04-.05s-.02-.04-.03-.06l-.16.67h-.14Z"/><path d="m3.96,15.49l.23-.96h.15l-.23.96h-.15Z"/><path d="m4.32,15.49l.23-.96h.52l-.03.13h-.37l-.06.24h.37l-.03.14h-.37l-.11.45h-.15Z"/><path d="m5.01,15.49l.23-.96h.15l-.23.96h-.15Z"/><path d="m5.38,15.49l.23-.96h.52l-.03.13h-.37l-.06.24h.37l-.03.14h-.37l-.07.31h.37l-.03.14h-.52Z"/><path d="m6.08,15.49l.23-.96h.2c.09,0,.19,0,.28.02.05.01.09.04.13.07.04.04.07.1.08.16.01.08,0,.15-.01.23-.02.08-.05.16-.1.23-.04.06-.1.12-.16.16-.05.03-.1.06-.16.07-.08.02-.16.02-.25.02h-.24Zm.19-.14h.13c.05,0,.11,0,.16-.01.04,0,.07-.03.1-.05.04-.03.08-.07.11-.12.03-.05.05-.1.06-.16.01-.05.02-.11.01-.16,0-.04-.02-.08-.05-.12-.02-.02-.05-.04-.08-.05-.05-.01-.11-.02-.17-.01h-.11l-.16.68Z"/><path d="m7.68,15.06l-.1.43h-.14l.23-.96h.23s.1,0,.15.01c.03,0,.05.02.07.04.03.02.04.06.05.09,0,.04,0,.08,0,.12-.01.04-.03.09-.06.13-.02.04-.05.07-.09.09-.03.02-.06.03-.09.04-.05,0-.1.01-.15.01h-.09,0Zm.04-.13h.04c.06,0,.12,0,.18-.03.04-.02.06-.06.07-.1.01-.04,0-.08-.02-.11-.05-.03-.1-.04-.16-.03h-.04l-.07.27Z"/><path d="m8.68,15.49l-.05-.25h-.37l-.17.25h-.16l.69-.99.21.99h-.16Zm-.33-.38h.26l-.04-.22s0-.03,0-.05c0-.02,0-.04,0-.06,0,.02-.02.04-.03.06,0,.02-.02.03-.03.05l-.15.22Z"/><path d="m9.06,15.49l.1-.44-.19-.52h.16l.12.33s0,.02,0,.03c0,.01,0,.03.01.04l.03-.04s.02-.03.03-.04l.28-.32h.15l-.44.52-.1.44h-.15Z"/><path d="m10.5,15.02s0-.04,0-.06c0-.02,0-.05,0-.07,0,.02-.02.04-.03.07-.01.02-.03.05-.04.07l-.37.51-.12-.52s0-.04-.01-.06c0-.02,0-.04,0-.06,0,.02-.01.04-.02.06,0,.02-.02.05-.03.07l-.21.47h-.14l.46-1,.13.6s0,.03,0,.05c0,.03,0,.06.01.08.02-.03.04-.06.07-.1,0,0,.01-.02.02-.03l.41-.6v1h-.15v-.48Z"/><path d="m10.75,15.49l.23-.96h.52l-.03.13h-.37l-.05.24h.37l-.03.14h-.37l-.07.31h.37l-.03.14h-.52Z"/><path d="m11.44,15.49l.24-.99.45.59s.02.03.04.05.02.04.04.06l.16-.66h.14l-.24.99-.46-.6-.03-.05s-.02-.04-.03-.06l-.16.67h-.14Z"/><path d="m12.99,14.67l-.2.82h-.15l.2-.82h-.25l.03-.13h.65l-.03.13h-.25Z"/><path d="m13.15,15.3l.13-.06s.01.07.04.1c.03.02.07.04.11.03.04,0,.08-.01.12-.04.03-.03.06-.06.06-.1.01-.06-.02-.11-.1-.15,0,0-.02-.01-.03-.01-.07-.03-.13-.08-.17-.14-.02-.05-.03-.1-.01-.15.02-.08.06-.14.12-.19.06-.05.14-.08.22-.07.06,0,.11.01.16.04.04.03.06.07.07.12l-.13.06s-.02-.05-.04-.07c-.02-.01-.05-.02-.07-.02-.04,0-.07.01-.1.03-.03.02-.05.05-.06.09-.01.06.03.11.12.16,0,0,.01,0,.02,0,.06.03.12.07.16.13.02.05.03.1.01.16-.02.09-.07.16-.14.22-.07.05-.16.08-.24.08-.07,0-.13-.02-.18-.06-.05-.04-.08-.1-.07-.16Z"/><path d="m14.27,15.49l.23-.96h.15l-.23.96h-.15Z"/><path d="m14.64,15.49l.24-.99.45.58s.02.03.04.05.02.04.04.06l.16-.66h.14l-.24.99-.46-.6-.04-.05s-.02-.04-.03-.06l-.16.67h-.14Z"/><path d="m16.19,14.67l-.2.82h-.15l.2-.82h-.25l.03-.13h.65l-.03.13h-.25Z"/><path d="m16.35,15.49l.23-.96h.52l-.03.13h-.37l-.06.24h.37l-.03.14h-.37l-.07.31h.37l-.03.14h-.52Z"/><path d="m17.29,15.06l-.1.43h-.14l.23-.96h.21s.09,0,.14.01c.03,0,.06.02.08.04.02.02.04.06.04.09,0,.04,0,.08,0,.12-.01.07-.05.13-.1.18-.05.04-.11.07-.18.08l.22.43h-.17l-.21-.43h-.02Zm.04-.12h.03c.06,0,.11,0,.17-.03.03-.02.06-.06.06-.1.01-.05,0-.09-.02-.11-.04-.03-.1-.04-.15-.03h-.03l-.06.27Z"/><path d="m17.78,15.49l.23-.96h.52l-.03.13h-.37l-.06.24h.37l-.03.14h-.37l-.11.45h-.15Z"/><path d="m19.03,15.49l-.05-.25h-.37l-.17.25h-.16l.69-.99.21,1h-.16Zm-.33-.38h.26l-.04-.22s0-.03,0-.05c0-.02,0-.04,0-.06,0,.02-.02.04-.03.06,0,.02-.02.03-.03.05l-.15.22Z"/><path d="m20.18,14.77s-.07-.07-.12-.08c-.05-.02-.1-.03-.14-.03-.1,0-.2.03-.27.1-.08.07-.14.16-.16.26-.03.09-.01.18.03.26.05.07.13.11.21.1.06,0,.11-.01.16-.03.06-.02.12-.05.17-.09l-.04.18s-.1.05-.15.07c-.05.01-.11.02-.16.02-.07,0-.13-.01-.19-.04-.05-.02-.1-.06-.13-.11-.04-.05-.06-.1-.07-.16,0-.07,0-.13.01-.2.02-.07.04-.14.08-.2.04-.06.09-.12.14-.16.05-.05.12-.08.19-.11.06-.02.13-.04.2-.04.05,0,.1,0,.15.02.05.02.09.04.13.08l-.04.16Z"/><path d="m20.2,15.49l.23-.96h.52l-.03.13h-.37l-.06.24h.37l-.03.14h-.37l-.07.31h.37l-.03.14h-.52Z"/><path d="m17.43,13.7h-1.04l1.45-5.24h1.04l-1.45,5.24Z"/><path d="m16.89,8.63c-.07-.1-.18-.15-.33-.15h-5.73l-.28,1.03h5.21l-.3,1.1h-5.21l-.86,3.12h1.04l.58-2.1h4.69c.15,0,.3-.05.41-.15.12-.09.21-.22.25-.37l.58-2.1c.05-.16.03-.28-.04-.38Z"/><path d="m8.76,13.37c-.06.21-.25.35-.46.35H2.92c-.13,0-.25-.05-.33-.15-.07-.11-.09-.25-.05-.37l1.31-4.72h1.04l-1.17,4.22h4.17l1.17-4.22h1.04l-1.36,4.89Z"/><path d="M19.75 8.48 21.07 11.1 18.3 13.72 19.75 8.48z"/><path d="M20.68 8.48 20.43 9.38 21.25 11.01 21.32 11.14 21.21 11.24 19.45 12.91 19.23 13.72 22 11.1 20.68 8.48z"/>
        </svg>
      )
    }
  ];

  const handleSubmit = async () => {
    if (!amount) {
      toast.error('Please enter amount');
      return;
    }

    const numAmount = Number(amount);
    if (numAmount < minimumWithdrawal) {
      toast.error(`Minimum withdrawal is ₹${minimumWithdrawal}`);
      return;
    }

    if (!selectedMethod) {
      toast.error('Please select a withdrawal method');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user ID from localStorage (adjust based on your auth implementation)
      const userId = localStorage.getItem('userId') || 1;
      
      // Call withdrawal API
      const response = await requestWithdrawal(userId, numAmount, selectedMethod);
      
      // Console log the response
      // console.log('Withdrawal API Response:', response);
      
      // Check if withdrawal was successful
      if (response.success) {
        toast.success('Withdrawal request submitted successfully');
        
        // Redirect to home after successful submission
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        // Show error message from response
        toast.error(response.message || 'Failed to process withdrawal');
      }
    } catch (error) {
      console.error('Withdrawal Error:', error);
      toast.error(error.message || 'Failed to process withdrawal');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="withdraw-funds-page">
      {/* Header */}
      <div className="withdraw-funds-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Withdraw Funds</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </div>

      {/* Content */}
      <div className="withdraw-funds-content">
        {/* Info Card */}
        <div className="info-card">
          <div className="info-text">MINIMUM WITHDRAWAL ₹{minimumWithdrawal}/-</div>
          <div className="info-text">MAXIMUM 1 LAKH PER DAY WITHDRAWAL</div>
          <div className="info-text">WITHDRAWAL TIME 10AM TO 5PM</div>
        </div>

        {/* Withdraw Input */}
        <div className="input-group">
          <div className="input-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7V12C2 16.5 5 20.5 12 22C19 20.5 22 16.5 22 12V7L12 2Z" stroke="#666" strokeWidth="2" fill="none"/>
            </svg>
          </div>
          <input
            type="text"
            inputMode="numeric"
            className="amount-input"
            placeholder="Withdraw Funds"
            value={amount}
            onChange={(e) => {
              // Only allow numeric characters
              const numericValue = e.target.value.replace(/[^0-9]/g, '');
              setAmount(numericValue);
            }}
          />
        </div>

        {/* Select Method */}
        <div className="select-method-section">
          <h2 className="section-title">Select Method</h2>
          
          <div className="payment-methods">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                className="payment-method-item"
                onClick={() => setSelectedMethod(method.id)}
              >
                <div className="method-info">
                  <div className="method-icon">{method.icon}</div>
                  <span className="method-name">{method.name}</span>
                </div>
                <div className={`radio-button ${selectedMethod === method.id ? 'selected' : ''}`}>
                  <div className="radio-inner"></div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <button 
          className="submit-btn" 
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'PROCESSING...' : 'SUBMIT'}
        </button>
      </div>
    </div>
  );
}

export default WithdrawFunds;
